import { cn } from "@/lib/utils.ts";
import { toUrnString } from "@/types/urn.ts";
import { useCallback, useLayoutEffect, useState } from "react";
import { CanvasArtifactModal } from "./CanvasArtifactModal.tsx";
import { CanvasContactModal } from "./CanvasContactModal.tsx";
import { CanvasNavBoundary } from "./CanvasNavBoundary.tsx";
import { CanvasParticipantsModal } from "./CanvasParticipantsModal.tsx";
import { CanvasRoutineModal } from "./CanvasRoutineModal.tsx";
import { CanvasCommunicationThreadModal } from "./CanvasCommunicationThreadModal.tsx";
import { CanvasSourceModal } from "./CanvasSourceModal.tsx";
import { CanvasHeaderUtilitiesProvider, useCanvasHeaderUtilityLayer } from "./canvasContext.tsx";
import type { ParsedCanvasContent } from "./canvasContent.ts";
import { isTypingOutside } from "./canvasFocus.ts";

/** The concrete modal for one stack layer, switched by kind. */
const CanvasLayer = ({
  layer,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
  onUnloadable,
}: {
  layer: ParsedCanvasContent | null;
  container: HTMLElement;
  showBack: boolean;
  isFullscreen: boolean;
  onBack: () => void;
  onClose: () => void;
  onUnloadable: () => void;
}) => {
  switch (layer?.kind) {
    case "source":
      return (
        <CanvasSourceModal
          sourceId={layer.id}
          container={container}
          showBack={showBack}
          isFullscreen={isFullscreen}
          onBack={onBack}
          onClose={onClose}
        />
      );
    case "artifact":
      return (
        <CanvasArtifactModal
          artifactId={layer.id}
          container={container}
          showBack={showBack}
          isFullscreen={isFullscreen}
          onBack={onBack}
          onClose={onClose}
          onUnloadable={onUnloadable}
        />
      );
    case "routine":
      return (
        <CanvasRoutineModal
          routineId={layer.id}
          container={container}
          showBack={showBack}
          isFullscreen={isFullscreen}
          onBack={onBack}
          onClose={onClose}
          onUnloadable={onUnloadable}
        />
      );
    case "contact":
      return (
        <CanvasContactModal
          contactId={layer.id}
          container={container}
          showBack={showBack}
          isFullscreen={isFullscreen}
          onBack={onBack}
          onClose={onClose}
        />
      );
    case "communicationThread":
      return (
        <CanvasCommunicationThreadModal
          communicationId={layer.id}
          container={container}
          showBack={showBack}
          isFullscreen={isFullscreen}
          onBack={onBack}
          onClose={onClose}
        />
      );
    case "threadParticipants":
      return (
        <CanvasParticipantsModal
          threadId={layer.id}
          container={container}
          showBack={showBack}
          isFullscreen={isFullscreen}
          onBack={onBack}
          onClose={onClose}
        />
      );
    default:
      return null;
  }
};

/**
 * One persistent stack layer and the portal target for its contained dialog.
 * Portalling back into this wrapper keeps `inert`, `aria-hidden`, and
 * `visibility:hidden` effective for lower layers without changing the shared
 * dialog implementation.
 */
const CanvasStackLayer = ({
  layer,
  isTop,
  isCanvasOpen,
  showBack,
  isFullscreen,
  onBack,
  onClose,
  onDropContent,
}: {
  layer: ParsedCanvasContent | null;
  isTop: boolean;
  isCanvasOpen: boolean;
  showBack: boolean;
  isFullscreen: boolean;
  onBack: () => void;
  onClose: () => void;
  onDropContent: (urn: string) => void;
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  // Inherited rather than assumed: a panel hosting a canvas of its own has
  // already named the layer its cards belong to.
  const headerUtilityLayer = useCanvasHeaderUtilityLayer();

  // Address this layer by the URN it was parsed from, so the drop targets the
  // right stack entry rather than a position. `parseCanvasContent` accepts only
  // canonical `urn:bb:<type>:<id>`, so recomposing returns the exact string the
  // stack holds.
  const onUnloadable = useCallback(() => {
    if (layer) onDropContent(toUrnString(layer));
  }, [layer, onDropContent]);

  // Radix focuses a card when it first mounts. A pop reveals an already-mounted
  // card, so move focus back into that newly interactive layer explicitly —
  // unless the user is typing elsewhere, which the card's own open leaves alone
  // for the same reason.
  useLayoutEffect(() => {
    if (!isCanvasOpen || !isTop || !container) return;
    const dialog = container.querySelector<HTMLElement>("[role=dialog]");
    if (!dialog || dialog.contains(document.activeElement)) return;
    if (isTypingOutside(container)) return;
    const focusTarget = dialog.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    (focusTarget ?? dialog).focus();
  }, [container, isCanvasOpen, isTop]);

  return (
    <div
      ref={setContainer}
      className={cn("absolute inset-0", !isTop && "invisible")}
      aria-hidden={!isTop || undefined}
      inert={!isTop}
    >
      {container && (
        // A link inside this card drills onto the stack, the same as one in the
        // conversation behind it. The boundary is repeated per layer rather than
        // relied on from the page because the dialog is portalled — context
        // reaches it through the React tree, but wouter's router does not.
        <CanvasNavBoundary>
          {/* What this wrapper's `invisible` and `inert` say in CSS, said in
              React for anything inside that has to know. `isCanvasOpen` as well
              as `isTop`, because the column this stack lives in stays mounted
              once collapsed — closing the last card leaves it here, on screen
              nowhere. And narrowing rather than setting, so a host whose canvas
              is not the screen's keeps its answer. */}
          <CanvasHeaderUtilitiesProvider value={isTop && isCanvasOpen ? headerUtilityLayer : null}>
            <CanvasLayer
              layer={layer}
              container={container}
              showBack={showBack}
              isFullscreen={isFullscreen}
              onBack={onBack}
              onClose={() => {
                if (isCanvasOpen) onClose();
              }}
              onUnloadable={onUnloadable}
            />
          </CanvasHeaderUtilitiesProvider>
        </CanvasNavBoundary>
      )}
    </div>
  );
};

/**
 * The canvas modal stack: renders every layer, but only the top is visible and
 * interactive — lower layers stay mounted, `invisible` + `inert`, so their scroll
 * position, in-progress edits, and query observers survive a drill-down-and-back
 * (and a taller lower card can never peek out behind the top one). Each card uses
 * the shared contained Radix dialog, portalled into its own layer wrapper so the
 * wrapper's hidden/inert state applies to the complete dialog and its scrim.
 *
 * Dismissal preserves the pre-stack dialog contract: scrim, close, and Escape
 * clear the stack; Back pops one layer.
 */
export const CanvasStack = ({
  layers,
  isCanvasOpen,
  isFullscreen,
  onPop,
  onClear,
  onDropContent,
}: {
  layers: (ParsedCanvasContent | null)[];
  /** The pane is expanded — gate focus restoration and dialog dismissal. */
  isCanvasOpen: boolean;
  /** Sticky display mode, forwarded to each layer's (gated) card toggle. */
  isFullscreen: boolean;
  onPop: () => void;
  onClear: () => void;
  /** Remove the layer addressing this URN — its target is gone for good. */
  onDropContent: (urn: string) => void;
}) => {
  const depth = layers.length;

  if (depth === 0) return null;

  return (
    <>
      {layers.map((layer, i) => {
        const isTop = i === depth - 1;
        return (
          <CanvasStackLayer
            // Content + index: the index keeps a cycle (a→b→a) from colliding,
            // while the content prefix forces a fresh card when a layer is
            // replaced in place (a resetTo swapping the root for a new entity)
            // rather than reusing the old card's mounted state.
            key={`${layer?.type ?? "none"}:${layer?.id ?? "none"}:${i}`}
            layer={layer}
            isTop={isTop}
            isCanvasOpen={isCanvasOpen}
            showBack={i > 0}
            isFullscreen={isFullscreen}
            onBack={onPop}
            onClose={onClear}
            onDropContent={onDropContent}
          />
        );
      })}
    </>
  );
};
